import React, { useEffect, useState, useMemo, useRef } from "react";
import { toast } from "react-hot-toast";
import { useHistory, useParams, useLocation } from "react-router-dom";
import {
  HiOutlineTrash,
  HiOutlinePlus,
  HiOutlineX,
  HiOutlineChevronUp,
  HiOutlineChevronDown,
  HiOutlineDotsVertical,
  HiOutlineSparkles,
  HiOutlineExclamation,
} from "react-icons/hi";
import { PiClockCounterClockwise } from "react-icons/pi";
import { LuArrowRight, LuLink2, LuListTodo, LuMessageCircleQuestion, LuSquareFunction } from "react-icons/lu";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useHotkeys } from "react-hotkeys-hook";
import API from "../../api";
import { SHORTCUTS } from "../../utils/shortcuts";
import { notifyShortcut } from "../../utils/shortcutTip";
import { EXAM_TYPE, EXAM_TYPE_LABEL, guidelineText } from "../../utils";
import { capture } from "../../instrument";
import DependencyExplorer from "../../components/dependencies/DependencyExplorer";
import CodeLookup from "../../components/CodeLookup";
import TableExamples from "../../components/table/TableExamples";
import useRecordNavShortcuts from "../../hooks/useRecordNavShortcuts";

const DEFAULT_EXAM_TYPE_PRIORITY = [
  EXAM_TYPE.DEFAULT,
  EXAM_TYPE.SANG,
  EXAM_TYPE.URINE,
  EXAM_TYPE.ECHANTILLON,
  EXAM_TYPE.H24,
  EXAM_TYPE.SEROLOGIE,
  EXAM_TYPE.PCR,
  EXAM_TYPE.SELLE,
  EXAM_TYPE.AUTRE,
];

const PCR_EXAM_TYPE_PRIORITY = [
  EXAM_TYPE.DEFAULT,
  EXAM_TYPE.SANG,
  EXAM_TYPE.URINE,
  EXAM_TYPE.ECHANTILLON,
  EXAM_TYPE.H24,
  EXAM_TYPE.PCR,
  EXAM_TYPE.SEROLOGIE,
  EXAM_TYPE.SELLE,
  EXAM_TYPE.AUTRE,
];

const CODE_TYPES = [
  { type: EXAM_TYPE.SANG, dotClass: "bg-red-500" },
  { type: EXAM_TYPE.SEROLOGIE, dotClass: "bg-yellow-500" },
  { type: EXAM_TYPE.PCR, dotClass: "bg-pink-500" },
  { type: EXAM_TYPE.URINE, dotClass: "bg-primary-500" },
  { type: EXAM_TYPE.SELLE, dotClass: "bg-violet-500" },
  { type: EXAM_TYPE.AUTRE, dotClass: "bg-orange-500" },
];

const EXAMS_TEXTAREA_CLASS =
  "w-full resize-y rounded-md border border-gray-200 bg-white px-3 py-2 text-sm leading-snug text-gray-900 placeholder:text-gray-300 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500";

const EditCode = () => {
  const history = useHistory();
  const { id } = useParams();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const [isTypeMenuOpen, setIsTypeMenuOpen] = useState(false);
  const typeMenuRef = useRef(null);
  const isDirtyRef = useRef(false);
  const markDirty = () => {
    isDirtyRef.current = true;
  };
  const [codes, setCodes] = useState({});
  const [exams, setExams] = useState({});
  const [examTypePriority, setExamTypePriority] = useState([]);
  const [suggestionData, setSuggestionData] = useState({ type: "", codes: [], exams: [] });
  const [linkedActions, setLinkedActions] = useState([]);
  const [linkedGuidelines, setLinkedGuidelines] = useState([]);
  const [linkedQuestions, setLinkedQuestions] = useState([]);
  const [relatedCodes, setRelatedCodes] = useState([]);

  const filteredIds = location.state?.filteredIds || null;
  const currentIndex = useMemo(() => (filteredIds ? filteredIds.indexOf(id) : -1), [filteredIds, id]);
  const hasNavigation = filteredIds !== null && currentIndex !== -1;

  const preview = useMemo(() => {
    const cached = queryClient
      .getQueriesData({ queryKey: ["tables"] })
      .flatMap(([, tables]) => tables || [])
      .find((t) => t._id === id);
    return cached ? { title: cached.codes.map((c) => c.value).join(", ") } : null;
  }, [id]);

  const navigateTo = (index) => {
    const targetId = filteredIds[index];
    history.replace({ pathname: `/table/${targetId}/edit`, state: { filteredIds } });
  };

  const navigatePrev = () => {
    if (!hasNavigation || currentIndex <= 0) return;
    if (isDirtyRef.current && !window.confirm("Vous avez des modifications non enregistrées. Voulez-vous vraiment quitter cette page ?")) return;
    navigateTo(currentIndex - 1);
  };

  const navigateNext = () => {
    if (!hasNavigation || currentIndex >= filteredIds.length - 1) return;
    if (isDirtyRef.current && !window.confirm("Vous avez des modifications non enregistrées. Voulez-vous vraiment quitter cette page ?")) return;
    navigateTo(currentIndex + 1);
  };

  const goBack = () => {
    if (isDirtyRef.current && !window.confirm("Vous avez des modifications non enregistrées. Voulez-vous vraiment quitter cette page ?")) return;
    history.push("/table");
  };

  useRecordNavShortcuts(
    {
      onPrev: navigatePrev,
      onNext: navigateNext,
      onBack: goBack,
      canPrev: hasNavigation && currentIndex > 0,
      canNext: hasNavigation && currentIndex < filteredIds?.length - 1,
    },
    [hasNavigation, currentIndex, filteredIds],
  );

  const currentCodes = useMemo(() => {
    return Object.values(codes)
      .flat()
      .filter(Boolean)
      .map((c) => c.value);
  }, [codes]);

  const codesToRetype = useMemo(() => {
    return Object.entries(codes).flatMap(([bucketType, codeObjects]) =>
      (codeObjects || []).filter((c) => c._id && c.type && c.type !== bucketType).map((c) => ({ _id: c._id, value: c.value, from: c.type, to: bucketType })),
    );
  }, [codes]);

  const retypeIds = codesToRetype
    .map((c) => c._id)
    .sort()
    .join(",");

  const { data: retypeTableCount } = useQuery({
    queryKey: ["codeReferences", { ids: retypeIds }],
    queryFn: async () => {
      const responses = await Promise.all(retypeIds.split(",").map((codeId) => API.get(`/code/${codeId}`)));
      return Object.fromEntries(
        responses.filter((response) => response.ok).map((response) => [response.data._id, (response.data.referencingTables || []).filter((table) => table._id !== id).length]),
      );
    },
    enabled: retypeIds.length > 0,
    staleTime: Infinity,
  });

  const {
    data: tableData,
    isLoading: isLoadingData,
    isError,
  } = useQuery({
    queryKey: ["table", id],
    queryFn: async () => {
      const response = await API.get(`/table/${id}`);
      if (!response.ok) throw new Error(response.message || "Une erreur est survenue lors du chargement du code");
      return response.data;
    },
    staleTime: Infinity,
  });

  useEffect(() => {
    if (!tableData) return;

    let newCodes = {};
    for (const code of tableData?.codes) {
      if (!newCodes[code.type]) newCodes[code.type] = [];
      newCodes[code.type].push({ _id: code._id, value: code.value, type: code.type });
    }

    let newExams = {};
    for (const exam of tableData.exams) {
      if (!newCodes[exam.type]) newCodes[exam.type] = [];
      if (!newExams[exam.type]) {
        newExams[exam.type] = exam.value;
        continue;
      }
      newExams[exam.type] = `${newExams[exam.type]}, ${exam.value}`;
    }

    if (
      newCodes[EXAM_TYPE.URINE] ||
      newCodes[EXAM_TYPE.ECHANTILLON] ||
      newCodes[EXAM_TYPE.H24] ||
      newExams[EXAM_TYPE.URINE] ||
      newExams[EXAM_TYPE.ECHANTILLON] ||
      newExams[EXAM_TYPE.H24]
    ) {
      newCodes[EXAM_TYPE.URINE] = newCodes[EXAM_TYPE.URINE] || [];
      newCodes[EXAM_TYPE.ECHANTILLON] = newCodes[EXAM_TYPE.ECHANTILLON] || [];
      newCodes[EXAM_TYPE.H24] = newCodes[EXAM_TYPE.H24] || [];
      newExams[EXAM_TYPE.URINE] = newExams[EXAM_TYPE.URINE] || "";
      newExams[EXAM_TYPE.ECHANTILLON] = newExams[EXAM_TYPE.ECHANTILLON] || "";
      newExams[EXAM_TYPE.H24] = newExams[EXAM_TYPE.H24] || "";
    }

    setCodes(newCodes);
    setExams(newExams);
    setExamTypePriority(tableData.EXAM_TYPE_PRIORITY);
    setLinkedQuestions(tableData.linkedQuestions);
    setLinkedGuidelines(tableData.linkedGuidelines);
    setLinkedActions(tableData.linkedActions || []);

    const allRelatedCodes = new Set();
    for (const code of tableData?.codes || []) {
      if (code.relatedCodes && code.relatedCodes.length > 0) {
        code.relatedCodes.filter(Boolean).forEach((rc) => allRelatedCodes.add(rc.value));
      }
    }
    setRelatedCodes(Array.from(allRelatedCodes).sort());
    isDirtyRef.current = false;
  }, [tableData]);

  useEffect(() => {
    if (!isError) return;
    toast.error("Une erreur est survenue lors du chargement du code");
    history.push("/table");
  }, [isError]);

  const { data: diagramData } = useQuery({
    queryKey: ["diagramData"],
    queryFn: async () => {
      const [questions, guidelines, actions] = await Promise.all([API.get("/question"), API.get("/guideline"), API.get("/action")]);
      if (!questions.ok || !guidelines.ok || !actions.ok) throw new Error("Failed to load diagram data");
      return { questions: questions.data, guidelines: guidelines.data, actions: actions.data };
    },
  });

  useEffect(() => {
    if (Object.keys(codes).length === 0) return;

    setExams((prev) => {
      const types = Object.keys(codes).filter((type) => codes[type] !== undefined);

      const examsToRemove = Object.keys(prev).filter((type) => !types.includes(type));
      const examsToAdd = types.filter((type) => prev[type] === undefined);

      let newExams = JSON.parse(JSON.stringify(prev));
      examsToRemove.forEach((type) => {
        newExams[type] = undefined;
      });

      examsToAdd.forEach((type) => {
        newExams[type] = "";
      });

      if (prev[EXAM_TYPE.DEFAULT]) newExams[EXAM_TYPE.DEFAULT] = prev[EXAM_TYPE.DEFAULT];
      else if (types.filter((type) => type !== EXAM_TYPE.ECHANTILLON && type !== EXAM_TYPE.H24).length > 1) newExams[EXAM_TYPE.DEFAULT] = "";
      return newExams;
    });

    if (codes[EXAM_TYPE.PCR] === undefined || codes[EXAM_TYPE.SEROLOGIE] === undefined) setExamTypePriority(DEFAULT_EXAM_TYPE_PRIORITY);
  }, [codes]);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const codesParams = urlParams.get("codes")?.split(",");
    const examsParams = urlParams.get("exams")?.split(",");

    const codeValues = codesParams?.map((c) => c.trim());
    const suggestionType = Object.keys(codes).find((type) => {
      const typeValues = (codes[type] || []).map((c) => c.value);
      return codeValues?.every((v) => typeValues.includes(v));
    });
    if (!suggestionType) return;

    const currentExams = exams[suggestionType]?.split(", ");
    const suggestionExams = examsParams?.map((exam) => exam.trim()) || [];

    const newExams = [...new Set([...[...currentExams, ...suggestionExams]])];
    setExams((prev) => ({
      ...prev,
      [suggestionType]: newExams.join(", "),
    }));
    setSuggestionData({ type: suggestionType, codes: codesParams, exams: suggestionExams });
  }, [codes]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
      if (typeMenuRef.current && !typeMenuRef.current.contains(event.target)) setIsTypeMenuOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const updateMutation = useMutation({
    mutationFn: (body) => API.put(`/table/${id}`, body),
    onSuccess: (response) => {
      if (!response.ok) {
        if (response.code === "CODE_ALREADY_EXISTS") return toast.error("Ce code existe déjà, choisissez-le dans les suggestions");
        if (response.code === "CODE_NOT_FOUND") return toast.error("Un des codes sélectionnés est introuvable");
        return toast.error(response.message || "Une erreur est survenue");
      }
      isDirtyRef.current = false;
      toast.success("Code modifié avec succès");
      queryClient.invalidateQueries({ queryKey: ["tables"] });
      queryClient.invalidateQueries({ queryKey: ["table", id] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["taskCount"] });
    },
    onError: (error) => {
      capture(error);
      toast.error("Une erreur est survenue");
    },
  });

  const restoreMutation = useMutation({
    mutationFn: () => API.put(`/table/${id}`, { deleted: false }),
    onSuccess: (response) => {
      if (!response.ok) return toast.error(response.message);
      toast.success("Table restaurée");
      queryClient.invalidateQueries({ queryKey: ["tables"] });
      queryClient.invalidateQueries({ queryKey: ["table", id] });
    },
    onError: (error) => {
      capture(error);
      toast.error("Une erreur est survenue");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => API.delete(`/table/${id}`),
    onSuccess: (response) => {
      if (!response.ok) return toast.error("Une erreur est survenue");
      toast.success("Code supprimé avec succès");
      queryClient.invalidateQueries({ queryKey: ["tables"] });
      history.push("/table");
    },
    onError: (error) => {
      capture(error);
      toast.error("Une erreur est survenue");
    },
  });

  const isMutating = updateMutation.isPending || deleteMutation.isPending;
  const isDeleted = !!tableData?.deleted;

  const editCode = () => {
    if (linkedActions.length > 0 || linkedGuidelines.length > 0 || linkedQuestions.length > 0) {
      if (!window.confirm("Certains éléments sont liés à ces codes. Voulez-vous vraiment les modifier ?")) return;
    }

    let body = { codes: [], exams: [], EXAM_TYPE_PRIORITY: examTypePriority };

    for (const [type, codeObjects] of Object.entries(codes)) {
      if (!codeObjects?.length) continue;
      body.codes.push(
        ...codeObjects.map((c) => ({
          _id: c._id,
          type,
          value: c.value,
        })),
      );
    }

    for (const [type, value] of Object.entries(exams)) {
      if (!value) continue;
      body.exams.push(
        ...value
          .split(",")
          .filter((value) => value.length > 0)
          .map((value) => ({ type, value: value.trim() })),
      );
    }

    if (!body.codes.length) return toast.error("Merci de saisir un code");
    updateMutation.mutate(body);
  };

  useHotkeys(SHORTCUTS.EDIT_SAVE.keys, () => editCode(), { preventDefault: true, enableOnFormTags: true, enabled: !isMutating && !isDeleted && !isLoadingData }, [
    editCode,
    isMutating,
    isDeleted,
    isLoadingData,
  ]);

  const deleteCode = () => {
    let confirmMessage = "Êtes-vous sûr de vouloir supprimer ces codes ?";
    if (linkedActions.length > 0 || linkedGuidelines.length > 0 || linkedQuestions.length > 0) {
      confirmMessage = "Certains éléments sont liés à ces codes. Voulez-vous vraiment les supprimer ?";
    }
    if (!window.confirm(confirmMessage)) return;
    deleteMutation.mutate();
  };

  const onAddType = (type) => {
    if (type === EXAM_TYPE.URINE) setCodes((prev) => ({ ...prev, [EXAM_TYPE.URINE]: [], [EXAM_TYPE.ECHANTILLON]: [], [EXAM_TYPE.H24]: [] }));
    else setCodes((prev) => ({ ...prev, [type]: [] }));
    markDirty();
  };

  const onRemoveType = (type) => {
    if (type === EXAM_TYPE.URINE) setCodes((prev) => ({ ...prev, [EXAM_TYPE.URINE]: undefined, [EXAM_TYPE.ECHANTILLON]: undefined, [EXAM_TYPE.H24]: undefined }));
    else setCodes((prev) => ({ ...prev, [type]: undefined }));
    markDirty();
  };

  const onChangeCodes = (type, newCodes) => {
    setCodes((prev) => ({ ...prev, [type]: newCodes }));
    markDirty();
  };

  const missingTypes = CODE_TYPES.filter(({ type }) => codes[type] === undefined);
  const fallbackType = examTypePriority.find((type) => codes[type]?.length > 0);
  const title = currentCodes.join(", ") || preview?.title || "Table";
  const audienceBadges = (q) => ["S", q.patient && "P", q.ide && "IDE"].filter(Boolean).join(" / ");

  return (
    <div className="flex h-[calc(100vh-60px)] flex-col bg-gray-50">
      {hasNavigation && (
        <div className="flex items-center gap-1.5 border-b border-gray-200 bg-white px-3 py-1.5">
          <button type="button" className="rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-600" onClick={goBack} title="Fermer (Esc)">
            <HiOutlineX className="h-4 w-4" />
          </button>
          <div className="mx-0.5 h-4 w-px bg-gray-200" />
          <button
            type="button"
            className="rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-gray-500"
            onClick={navigatePrev}
            disabled={currentIndex <= 0}
          >
            <HiOutlineChevronUp className="h-4 w-4" />
          </button>
          <button
            type="button"
            className="rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-gray-500"
            onClick={navigateNext}
            disabled={currentIndex >= filteredIds.length - 1}
          >
            <HiOutlineChevronDown className="h-4 w-4" />
          </button>
          <span className="ml-1 text-xs tabular-nums text-gray-500">
            {currentIndex + 1} / {filteredIds.length}
          </span>
        </div>
      )}

      {isDeleted && (
        <div className="flex items-center gap-3 border-b border-gray-200 bg-gray-100 px-8 py-2.5">
          <HiOutlineTrash className="h-4 w-4 shrink-0 text-gray-500" />
          <p className="text-sm text-gray-600">Cette table est supprimée : elle est affichée en lecture seule. Restaurez-la pour pouvoir la modifier.</p>
        </div>
      )}

      <div className="flex items-start justify-between border-b border-gray-200 bg-white px-8 pb-5 pt-6">
        <div className="flex min-w-0 items-center gap-3">
          <h1 className="truncate text-lg font-semibold text-gray-900">{title}</h1>
          {isDeleted && (
            <span className="inline-flex shrink-0 items-center gap-1 rounded-md border border-gray-300 bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
              <HiOutlineTrash className="h-3 w-3" />
              Supprimée
            </span>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1.5 pl-4">
          <div className="relative" ref={menuRef}>
            <button type="button" className="rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-600" onClick={() => setIsMenuOpen(!isMenuOpen)}>
              <HiOutlineDotsVertical className="h-5 w-5" />
            </button>
            {isMenuOpen && (
              <div className="absolute right-0 z-10 mt-1 w-48 rounded-lg border border-gray-200 bg-white shadow-lg">
                <button
                  type="button"
                  className="flex w-full items-center gap-2 rounded-t-lg px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50"
                  onClick={() => {
                    setIsMenuOpen(false);
                    window.open(`/audit-log/table2/${id}`, "_blank", "noopener,noreferrer,width=1000,height=800");
                  }}
                >
                  <PiClockCounterClockwise className="h-4 w-4" />
                  Historique
                </button>
                {!isDeleted && (
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 rounded-b-lg px-3 py-2 text-sm text-red-600 transition-colors hover:bg-red-50"
                    onClick={() => {
                      setIsMenuOpen(false);
                      deleteCode();
                    }}
                    disabled={isMutating}
                  >
                    <HiOutlineTrash className="h-4 w-4" />
                    Supprimer
                  </button>
                )}
              </div>
            )}
          </div>
          {isDeleted ? (
            <button
              type="button"
              className="rounded-lg bg-primary-500 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50"
              onClick={() => restoreMutation.mutate()}
              disabled={restoreMutation.isPending}
            >
              {restoreMutation.isPending ? "Restauration..." : "Restaurer"}
            </button>
          ) : (
            <button
              type="button"
              className="rounded-lg bg-primary-500 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50"
              onClick={() => {
                editCode();
                notifyShortcut("EDIT_SAVE");
              }}
              disabled={isMutating || isLoadingData}
            >
              {updateMutation.isPending ? "Enregistrement..." : "Enregistrer"}
            </button>
          )}
        </div>
      </div>

      {isLoadingData ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-primary-500" />
          <span className="text-sm text-gray-400">Chargement...</span>
        </div>
      ) : (
        <div className="grid flex-1 grid-cols-5 overflow-hidden">
          <div className={`col-span-3 flex-1 overflow-y-auto ${isDeleted ? "pointer-events-none opacity-60" : ""}`} onChangeCapture={markDirty}>
            <fieldset disabled={isDeleted} className="mx-auto max-w-4xl px-8 py-6">
              {suggestionData.exams?.length > 0 && suggestionData.type && (
                <div className="mb-6 flex items-start gap-3 rounded-lg border border-primary-200 bg-primary-50 px-4 py-3">
                  <HiOutlineSparkles className="mt-0.5 h-4 w-4 shrink-0 text-primary-500" />
                  <div className="min-w-0">
                    <p className="text-sm text-primary-700">
                      {suggestionData.exams.length > 1 ? "Examens suggérés" : "Examen suggéré"} pour le code{" "}
                      <span className="font-medium">{suggestionData.codes.join(", ")}</span>, à ajouter dans la catégorie{" "}
                      <span className="font-medium">{EXAM_TYPE_LABEL[suggestionData.type] || suggestionData.type}</span>. Enregistrez pour valider.
                    </p>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {suggestionData.exams.map((exam, index) => (
                        <span key={index} className="rounded bg-white px-1.5 py-0.5 text-xs font-medium text-primary-700 ring-1 ring-primary-200">
                          {exam}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <h3 className="mb-4 text-lg font-medium text-gray-900">Détails</h3>

              <div className="space-y-3">
                <div className="flex items-baseline justify-between px-1">
                  <h4 className="text-sm font-medium text-gray-700">Codes par type</h4>
                  <p className="text-xs text-gray-400">Examens séparés par une virgule</p>
                </div>

                {CODE_TYPES.filter(({ type }) => codes[type] !== undefined).map(({ type, dotClass }) => (
                  <div key={type} className="rounded-lg border border-gray-100 bg-white">
                    <div className="flex flex-wrap items-center gap-2.5 rounded-t-lg border-b border-gray-100 bg-gray-50/40 px-5 py-2.5">
                      <span className={`h-2 w-2 shrink-0 rounded-full ${dotClass}`} />
                      <span className="text-[13px] font-medium text-gray-800">{EXAM_TYPE_LABEL[type]}</span>
                      <span className="text-xs text-gray-400">
                        {type === EXAM_TYPE.URINE
                          ? "Ces examens donnent toujours des codes urinaires, quel que soit le contexte de l'ordonnance"
                          : `Ces examens donnent toujours les codes ${EXAM_TYPE_LABEL[type]}, quel que soit le contexte de l'ordonnance`}
                      </span>
                      <button
                        type="button"
                        onClick={() => onRemoveType(type)}
                        className="ml-auto rounded p-1 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-500"
                        title={`Supprimer le type ${EXAM_TYPE_LABEL[type]}`}
                      >
                        <HiOutlineX className="h-4 w-4" />
                      </button>
                    </div>

                    {type === EXAM_TYPE.URINE ? (
                      <div className="space-y-4 px-5 py-4">
                        <div>
                          <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wide text-gray-400">Examens communs à Échantillon et 24 heures</label>
                          <textarea
                            rows={2}
                            className={EXAMS_TEXTAREA_CLASS}
                            placeholder="Examen"
                            value={exams[EXAM_TYPE.URINE] || ""}
                            onChange={(e) => setExams((prev) => ({ ...prev, [EXAM_TYPE.URINE]: e.target.value }))}
                          />
                          <p className="mt-1.5 text-xs text-gray-400">L'ordonnance choisit 24 heures ou Échantillon, sinon Échantillon</p>
                        </div>
                        <div className="space-y-4 border-t border-gray-100 pt-4">
                          <div className="flex items-center gap-2">
                            <span className="h-1.5 w-1.5 rounded-full bg-purple-500" />
                            <span className="text-[13px] font-medium text-gray-800">Échantillon (miction)</span>
                          </div>
                          <div>
                            <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wide text-gray-400">Codes</label>
                            <CodeLookup
                              initialValues={codes[EXAM_TYPE.ECHANTILLON] || []}
                              onChange={(newCodes) => onChangeCodes(EXAM_TYPE.ECHANTILLON, newCodes)}
                              placeholder="Code"
                            />
                          </div>
                          <div>
                            <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wide text-gray-400">Examens</label>
                            <textarea
                              rows={2}
                              className={EXAMS_TEXTAREA_CLASS}
                              placeholder="Examen"
                              value={exams[EXAM_TYPE.ECHANTILLON] || ""}
                              onChange={(e) => setExams((prev) => ({ ...prev, [EXAM_TYPE.ECHANTILLON]: e.target.value }))}
                            />
                          </div>
                        </div>
                        <div className="space-y-4 border-t border-gray-100 pt-4">
                          <div className="flex items-center gap-2">
                            <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                            <span className="text-[13px] font-medium text-gray-800">24 heures</span>
                          </div>
                          <div>
                            <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wide text-gray-400">Codes</label>
                            <CodeLookup initialValues={codes[EXAM_TYPE.H24] || []} onChange={(newCodes) => onChangeCodes(EXAM_TYPE.H24, newCodes)} placeholder="Code" />
                          </div>
                          <div>
                            <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wide text-gray-400">Examens</label>
                            <textarea
                              rows={2}
                              className={EXAMS_TEXTAREA_CLASS}
                              placeholder="Examen"
                              value={exams[EXAM_TYPE.H24] || ""}
                              onChange={(e) => setExams((prev) => ({ ...prev, [EXAM_TYPE.H24]: e.target.value }))}
                            />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4 px-5 py-4">
                        <div>
                          <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wide text-gray-400">Codes</label>
                          <CodeLookup initialValues={codes[type] || []} onChange={(newCodes) => onChangeCodes(type, newCodes)} placeholder="Code" />
                        </div>
                        <div>
                          <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wide text-gray-400">Examens</label>
                          <textarea
                            rows={2}
                            className={EXAMS_TEXTAREA_CLASS}
                            placeholder="Examen"
                            value={exams[type] || ""}
                            onChange={(e) => setExams((prev) => ({ ...prev, [type]: e.target.value }))}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ))}

                {exams[EXAM_TYPE.DEFAULT] !== undefined && (
                  <div className="rounded-lg border border-gray-100 bg-white">
                    <div className="flex flex-wrap items-center gap-2.5 rounded-t-lg border-b border-gray-100 bg-gray-50/40 px-5 py-2.5">
                      <span className="h-2 w-2 shrink-0 rounded-full bg-gray-400" />
                      <span className="text-[13px] font-medium text-gray-800">Examens communs à tous les types</span>
                      <span className="text-xs text-gray-400">Le contexte de l'ordonnance choisit le type{fallbackType ? `, sinon ${EXAM_TYPE_LABEL[fallbackType]}` : ""}</span>
                    </div>
                    <div className="px-5 py-4">
                      <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wide text-gray-400">Examens</label>
                      <textarea
                        rows={2}
                        className={EXAMS_TEXTAREA_CLASS}
                        placeholder="Examen"
                        value={exams[EXAM_TYPE.DEFAULT] || ""}
                        onChange={(e) => setExams((prev) => ({ ...prev, [EXAM_TYPE.DEFAULT]: e.target.value }))}
                      />
                    </div>
                  </div>
                )}

                {missingTypes.length > 0 && (
                  <div className="relative px-1 pt-1" ref={typeMenuRef}>
                    <button
                      type="button"
                      onClick={() => setIsTypeMenuOpen(!isTypeMenuOpen)}
                      className="flex items-center gap-1.5 rounded-md border border-dashed border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:border-primary-400 hover:bg-primary-50 hover:text-primary-700"
                    >
                      <HiOutlinePlus className="h-3.5 w-3.5" />
                      Ajouter un type
                    </button>
                    {isTypeMenuOpen && (
                      <div className="absolute left-1 z-10 mt-1 w-44 rounded-lg border border-gray-200 bg-white shadow-lg">
                        {missingTypes.map(({ type, dotClass }) => (
                          <button
                            key={type}
                            type="button"
                            className="flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-700 transition-colors first:rounded-t-lg last:rounded-b-lg hover:bg-gray-50"
                            onClick={() => {
                              setIsTypeMenuOpen(false);
                              onAddType(type);
                            }}
                          >
                            <span className={`h-1.5 w-1.5 rounded-full ${dotClass}`} />
                            {EXAM_TYPE_LABEL[type]}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {exams[EXAM_TYPE.PCR] !== undefined && exams[EXAM_TYPE.SEROLOGIE] !== undefined && (
                <div className="mt-6 rounded-lg border border-gray-100 bg-white p-5">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="pcrPriority"
                      className="h-5 w-5 cursor-pointer rounded border-gray-300 text-primary-500 focus:ring-2 focus:ring-primary-500"
                      checked={JSON.stringify(examTypePriority) === JSON.stringify(PCR_EXAM_TYPE_PRIORITY)}
                      onChange={(e) => setExamTypePriority(e.target.checked ? PCR_EXAM_TYPE_PRIORITY : DEFAULT_EXAM_TYPE_PRIORITY)}
                    />
                    <label htmlFor="pcrPriority" className="ml-3 cursor-pointer text-sm font-medium text-gray-700">
                      Codes PCR prioritaires sur les codes Sérologie
                    </label>
                  </div>
                  <p className="ml-8 mt-2 text-xs text-gray-500">En l'absence de contexte dans l'ordonnance, les codes PCR sont insérés à la place des codes Sérologie.</p>
                </div>
              )}

              {relatedCodes.length > 0 && (
                <div className="mt-6 flex gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <LuLink2 className="mt-0.5 h-5 w-5 shrink-0 text-gray-400" />
                  <p className="text-xs leading-relaxed text-gray-600">
                    <span className="font-medium text-gray-700">Codes liés au groupe : </span>
                    {relatedCodes.join(", ")}
                  </p>
                </div>
              )}

              {codesToRetype.length > 0 && (
                <div className="mt-6 flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
                  <HiOutlineExclamation className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
                  <div className="text-xs leading-relaxed text-amber-800">
                    <p className="font-medium">L'enregistrement va changer le type de ces codes :</p>
                    <ul className="mt-1 list-disc space-y-1 pl-5">
                      {codesToRetype.map((code) => (
                        <li key={code._id}>
                          <span className="font-medium">{code.value}</span> : {EXAM_TYPE_LABEL[code.from] || code.from} → {EXAM_TYPE_LABEL[code.to] || code.to}
                          {retypeTableCount?.[code._id] > 0 &&
                            ` — ce changement affecte aussi ${retypeTableCount[code._id]} autre${retypeTableCount[code._id] > 1 ? "s" : ""} table${
                              retypeTableCount[code._id] > 1 ? "s" : ""
                            }`}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              <TableExamples codes={codes} exams={exams} examTypePriority={examTypePriority} />

              {(linkedActions.length > 0 || linkedGuidelines.length > 0 || linkedQuestions.length > 0) && (
                <div className="mt-8 space-y-6">
                  {linkedActions.length > 0 && (
                    <div className="rounded-lg border border-orange-200 bg-orange-50 p-4">
                      <div className="mb-3 flex items-center">
                        <LuSquareFunction className="mr-2 h-5 w-5 text-orange-500" />
                        <div className="text-sm font-medium text-orange-700">Actions liées ({linkedActions.length})</div>
                      </div>
                      <div className="space-y-2">
                        {linkedActions.map((action) => (
                          <div
                            key={action._id}
                            className="group cursor-pointer rounded-md border border-orange-100 bg-white p-3 transition-all hover:border-orange-500"
                            onClick={() => window.open(`/action/${action._id}/edit`, "_blank")}
                          >
                            <div className="flex items-center justify-between">
                              <div className="text-sm text-gray-700">{action.name || "—"}</div>
                              <LuArrowRight className="ml-1 shrink-0 text-orange-500 transition-transform group-hover:translate-x-0.5" />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {linkedGuidelines.length > 0 && (
                    <div className="rounded-lg border border-green-200 bg-green-50 p-4">
                      <div className="mb-3 flex items-center">
                        <LuListTodo className="mr-2 h-5 w-5 text-green-500" />
                        <div className="text-sm font-medium text-green-700">Recommandations liées ({linkedGuidelines.length})</div>
                      </div>
                      <div className="space-y-2">
                        {linkedGuidelines.map((guideline) => (
                          <div
                            key={guideline._id}
                            className="group cursor-pointer rounded-md border border-green-100 bg-white p-3 transition-all hover:border-green-500"
                            onClick={() => window.open(`/guideline/${guideline._id}/edit`, "_blank")}
                          >
                            <div className="flex items-center justify-between">
                              <div className="line-clamp-2 whitespace-pre-wrap text-sm text-gray-700">{guidelineText(guideline)}</div>
                              <LuArrowRight className="ml-1 shrink-0 text-green-500 transition-transform group-hover:translate-x-0.5" />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {linkedQuestions.length > 0 && (
                    <div className="rounded-lg border border-purple-200 bg-purple-50 p-4">
                      <div className="mb-3 flex items-center">
                        <LuMessageCircleQuestion className="mr-2 h-5 w-5 text-purple-500" />
                        <div className="text-sm font-medium text-purple-700">Questions liées ({linkedQuestions.length})</div>
                      </div>
                      <div className="space-y-2">
                        {linkedQuestions.map((question) => (
                          <div
                            key={question._id}
                            className="group cursor-pointer rounded-md border border-purple-100 bg-white p-3 transition-all hover:border-purple-500"
                            onClick={() => window.open(`/question/${question._id}/edit`, "_blank")}
                          >
                            <div className="flex items-center justify-between">
                              <div className="text-sm text-gray-700">{question.question || question._id}</div>
                              <LuArrowRight className="ml-1 shrink-0 text-purple-500 transition-transform group-hover:translate-x-0.5" />
                            </div>
                            <div className="mt-2 flex flex-wrap items-start gap-2">
                              <div className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">{question.questionCode || question._id}</div>
                              <div className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">{audienceBadges(question)}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </fieldset>
          </div>

          <div className="col-span-2 shrink-0 overflow-y-auto border-l border-gray-200 bg-white p-6">
            <h3 className="mb-4 text-lg font-medium text-gray-900">Dépendances</h3>
            {diagramData ? (
              <DependencyExplorer data={diagramData} currentCodes={currentCodes} className="rounded-lg border border-gray-100" />
            ) : (
              <div className="flex items-center justify-center rounded-lg border border-gray-100 py-8 text-sm text-gray-400">Chargement du diagramme...</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EditCode;
